import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight';
import { CLOSE_TO_SEARCH, MY_DRAWN_AREA, MY_TRAVEL_TIME_AREA } from '@propertypal/shared/src/constants/search';
import useRecentSearches from '@propertypal/shared/src/hooks/useRecentSearches';
import useSearchState from '@propertypal/shared/src/hooks/useSearchState';
import { getNlpUrl } from '@propertypal/shared/src/reducers/search';
import { trackGaEvent, trackSearch } from '@propertypal/shared/src/services/analytics';
import { SuperFeatureProperty } from '@propertypal/shared/src/types/superFeature';
import { Filters } from '@propertypal/shared/src/utils/search/getNlpFilters';
import { Suggestion } from '@propertypal/shared/src/utils/search/groupTokensByType';
import SearchPreviewModal from '@propertypal/web-app/src/components/search/SearchPreviewModal';
import getCurrentLocation from '@propertypal/web-app/src/services/location';
import { useRouter } from 'next/router';
import React, { FormEvent, FunctionComponent, useEffect, useRef, useState } from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useTheme } from 'styled-components';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import Button from '../buttons/Button';
import useCloseToSearch from '../close-search/useCloseToSearch';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import SearchInput from '../inputs/SearchInput';
import CloseToSearchModal from '../modal/CloseToSearchModal.pn';
import PropertyPrice from '../property/PropertyPrice';
import {
  AccountLogo,
  CloseToContainer,
  CloseToContent,
  CloseToIcon,
  CloseToIconWrapper,
  CloseToTrigger,
  CloseToTriggerWrapper,
  SearchBox,
  SearchInputWrapper,
  SlidePropertyDetails,
  SliderTextBox,
  SlideShowBox,
  SlideShowImage,
  StarBurstIcon,
  TabItem,
  TabRow,
  Wrapper,
} from './HomeHeader.style';

interface Props {
  superfeatures: SuperFeatureProperty[];
  loggedIn: boolean;
  cfIpCountry: string | null;
}

const HomeHeader: FunctionComponent<Props> = (props) => {
  const { handleChangeText, nlpData, searchText, getLocation, fetchingLocation } = useSearchState();
  const [tab, setTab] = useState('residential:sale');
  const [showSearchPreview, setShowSearchPreview] = useState('');
  const { recentSearches, removeRecentSearch } = useRecentSearches();
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const tabRowRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const router = useRouter();
  const { closeToModal, handleCloseToClick, onCloseCloseToModal } = useCloseToSearch({
    page: 'HP',
    router,
    loggedIn: props.loggedIn,
  });

  const handlePropertySearch = async (baseUrl: string, text: string, filters: Filters) => {
    setLoading(true);
    const url = await getNlpUrl(text, filters);

    if (url) {
      trackGaEvent('nav_click', {
        page: 'HP',
        location: 'Search Box',
        button: filters.category === 'newhomes' ? 'New Homes' : filters.saleType === 'rent' ? 'For Rent' : 'For Sale',
      });
      trackSearch('HP', text, filters);

      await router.push(baseUrl + url);
    }
  };

  const handleLocationClick = async (position: 'suggestion' | 'inline') => {
    trackGaEvent('current_location_click', {
      page: 'HP',
      location: 'Search Box',
      position,
    });
    const location = await getLocation(getCurrentLocation, true);
    const [category, saleType] = tab.split(':');

    await router.push(
      `/search?runit=m&rlat=${location.lat}&rlng=${location.lng}&st=${saleType}&pt=${category}&nl=My+Location`,
    );
  };

  const handleTravelTimeClick = (position: 'suggestion' | 'inline') => {
    trackGaEvent('travel_time_click', {
      page: 'HP',
      location: 'Search Box',
      position,
    });
    setShowSearchPreview('travelTime');
  };

  const handleDrawSearchClick = (position: 'suggestion' | 'inline') => {
    trackGaEvent('draw_click', {
      page: 'HP',
      location: 'Search Box',
      position,
    });
    setShowSearchPreview('drawSearch');
  };

  const handlePreviewSubmit = (polyIds: number[], category: string, saleType: string) => {
    handlePropertySearch('/map', showSearchPreview === 'drawSearch' ? MY_DRAWN_AREA : MY_TRAVEL_TIME_AREA, {
      category,
      saleType,
      polyIds,
    });
  };

  const handleSubmit = (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const [category, saleType] = tab.split(':');

    if (category === 'newhomes' && searchText.trim() === '' && props.cfIpCountry !== 'IE') {
      router.push('/new-homes/northern-ireland');
    } else {
      handlePropertySearch('', searchText, { category, saleType });
    }
  };

  const handleSuggestionClick = (token: Suggestion) => {
    if (token.url) {
      router.push(token.url);
    } else {
      handleChangeText(token.value);
    }
  };

  const onCloseToSearch = async (polyIds: number[]) => {
    const [category, saleType] = tab.split(':');
    await handlePropertySearch('', CLOSE_TO_SEARCH, {
      category,
      saleType,
      polyIds,
      intersection: true,
    });
    return true;
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <Wrapper>
      <SearchBox action="" onSubmit={handleSubmit}>
        <h1>
          Find properties for sale and for rent in Northern Ireland
          <StarBurstIcon />
        </h1>

        <TabRow ref={tabRowRef}>
          <TabItem $active={tab === 'residential:sale'} onClick={() => setTab('residential:sale')} type="button">
            For Sale
          </TabItem>
          <TabItem $active={tab === 'residential:rent'} onClick={() => setTab('residential:rent')} type="button">
            For Rent
          </TabItem>
          <TabItem $active={tab === 'newhomes:sale'} onClick={() => setTab('newhomes:sale')} type="button">
            New Homes
          </TabItem>
        </TabRow>

        <SearchInputWrapper>
          <SearchInput
            onFocus={() => {
              // Timeout enables this feature for ios safari
              // safari will also attempt to scroll to an input, but we want this called after their scroll event
              setTimeout(() => {
                if (tabRowRef.current && window.innerWidth < 700) {
                  window.scrollTo({
                    top: tabRowRef.current.getBoundingClientRect().top + window.scrollY - 8,
                    behavior: 'smooth',
                  });
                }
              }, 50);
            }}
            onChangeText={handleChangeText}
            value={searchText}
            nlpData={nlpData}
            recentSearches={recentSearches}
            onRecentSearchClick={(s, f) => handlePropertySearch('', s, f)}
            onRemoveRecentSearch={removeRecentSearch}
            onLocationClick={handleLocationClick}
            onSuggestionClick={handleSuggestionClick}
            onTravelTimeClick={handleTravelTimeClick}
            onDrawSearchClick={handleDrawSearchClick}
            onCloseToClick={handleCloseToClick}
            loading={fetchingLocation}
          />
        </SearchInputWrapper>

        <Button type="submit" loading={loading}>
          Search
        </Button>

        <CloseToContainer>
          <CloseToIconWrapper>
            <CloseToIcon />
          </CloseToIconWrapper>
          <CloseToContent>
            <div>
              <span>Search by travel time to the places that matter most</span>
            </div>
            <CloseToTriggerWrapper>
              <CloseToTrigger
                type="button"
                onClick={() => handleCloseToClick(true)}
                data-testid="trigger-close-to-modal"
              >
                <FontAwesomeIcon icon={faArrowRight} />
                <span>Try our &apos;close to&apos; search</span>
              </CloseToTrigger>
            </CloseToTriggerWrapper>
          </CloseToContent>
        </CloseToContainer>
      </SearchBox>

      <SlideShowBox>
        {mounted && (
          <Swiper modules={[Navigation]} navigation loop>
            {props.superfeatures.map((superfeature) => (
              <SwiperSlide key={superfeature.fallback ? superfeature.url! : superfeature.image.url}>
                <SlideShowImage
                  href={superfeature.path || ''}
                  $url={superfeature.fallback ? superfeature.url! : superfeature.image.url}
                />

                {superfeature.path && (
                  <SlidePropertyDetails data-testid="superfeature-property-details">
                    {superfeature?.listingLogos?.map((logo) => (
                      <AccountLogo
                        key={logo.standard}
                        style={{
                          backgroundImage: `url(${logo.standard})`,
                          backgroundColor: logo.standardBackgroundColour,
                        }}
                      />
                    ))}

                    <SliderTextBox href={superfeature.path}>
                      {superfeature.displayAddress}
                      {' - '}
                      <PropertyPrice
                        as="span"
                        price={superfeature.price}
                        fontSize={16}
                        primaryColor={theme.textDark}
                        secondaryColor={theme.textDark}
                      />
                    </SliderTextBox>
                  </SlidePropertyDetails>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </SlideShowBox>

      {showSearchPreview && (
        <SearchPreviewModal
          show={showSearchPreview}
          onClose={() => setShowSearchPreview('')}
          onSubmit={handlePreviewSubmit}
          showSaleTypeOptions
        />
      )}

      {closeToModal && (
        <CloseToSearchModal show={closeToModal} onClose={onCloseCloseToModal} onCloseToSearch={onCloseToSearch} />
      )}
    </Wrapper>
  );
};

export default HomeHeader;
